/**
 * MobileAIStorage Component for LOOPINO AI Platform
 * Mobile-optimized AI Storage screen for managing projects and files
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './MobileAIStorage.css';
import { buildMediaCounters } from './utils';
import CreateProjectModal from './CreateProjectModal';
import ProjectDetailPage from './ProjectDetailPage';
import useLanguage from '../../../hooks/useLanguage';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import aiStorageService from '../../../services/AIStorageService';

// Import icons
import iconAI from '../../../assets/images/icons/icon-ai.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import ImageGalleryPage from '../MobileImageGallery/ImageGalleryPage';
import MobileGalleryPreview from '../MobileImageGallery/MobileGalleryPreview';
import { Skeleton } from '../../effects';

const MobileAIStorage = ({ 
  onBack, 
  currentView = 'list',
  selectedProjectId = null
}) => {
  const { t, language } = useLanguage();
  const { confirm } = useMobileAlert();
  // State management
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(selectedProjectId);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());

  const projectDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (err) {
      console.warn('[MobileAIStorage] Falling back to default date formatter', err);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const formatProjectDate = useCallback((date) => {
    if (!date) {
      return '--';
    }

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return '--';
    }

    return projectDateFormatter.format(parsedDate);
  }, [projectDateFormatter]);

  const setImageLoadingState = useCallback((key, status) => {
    if (!key) {
      return;
    }

    setImageLoadingStates((prev) => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, []);

  const getImageLoadingProps = useCallback((source, scope = 'thumbnail') => {
    const normalizedScope = scope || 'thumbnail';
    const baseKey = typeof source === 'string' && source.length > 0
      ? source
      : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';

    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error')
    };
  }, [imageLoadingStates, setImageLoadingState]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const normalizedProjects = await aiStorageService.getProjects({
        page: 1,
        pageSize: 50,
        includeArchived: false,
        previewLimit: 8,
      });
      setProjects(normalizedProjects);
    } catch (err) {
      console.error('[MobileAIStorage] Failed to load projects', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.list.errors.load', { error: message }));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Handle project creation
  const handleCreateProject = useCallback(() => {
    console.log('[MobileAIStorage] Creating new project');
    setShowCreateModal(true);
  }, []);

  // Handle project creation completion
  const handleProjectCreated = useCallback((newProject) => {
    console.log('[MobileAIStorage] Project created:', newProject);

    setShowCreateModal(false);

    if (!newProject) {
      loadProjects();
      return;
    }

    setProjects((prev) => {
      const filtered = prev.filter((project) => project.id !== newProject.id);
      return [newProject, ...filtered];
    });

    setCurrentProjectId(newProject.id);
  }, [loadProjects]);

  // Handle project click
  const handleProjectClick = useCallback((project) => {
    console.log('[MobileAIStorage] Project clicked:', project);
    setCurrentProjectId(project.id);
  }, []);

  // Handle back navigation
  const handleBackToList = useCallback(() => {
    setCurrentProjectId(null);
  }, []);

  const handleOpenGalleryPreview = useCallback(() => {
    setShowImageGallery(true);
  }, []);

  const handleCloseGalleryPreview = useCallback(() => {
    setShowImageGallery(false);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  // Handle project deletion
  const handleDeleteProject = useCallback(async (projectId, event) => {
    event.stopPropagation(); // Prevent project click

    // Confirm deletion
    const confirmed = await confirm({
      title: t('aiStorage.messages.deleteProjectConfirm'),
      variant: 'warning',
      confirmText: t('aiStorage.actions.delete', { defaultValue: 'Elimina' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });
    if (!confirmed) return;

    try {
      await aiStorageService.deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      console.error('[MobileAIStorage] Failed to delete album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.list.errors.delete', { error: message }));
    }
  }, [confirm, t]);

  // Render project card
  const renderProjectCard = (project) => {
    const projectDescription = project.description || '';
    const rawTitle = (project.title || '').trim();
    const fallbackTitles = ['Album senza titolo', 'Untitled album'];
    const displayTitle = rawTitle && !fallbackTitles.includes(rawTitle)
      ? project.title
      : t('aiStorage.list.defaults.untitledAlbum');
    const thumbnailScope = `project-${project.id || 'unknown'}`;

    return (
      <div 
        key={project.id}
        className="mobile-ai-project-card"
        onClick={() => handleProjectClick(project)}
      >
        <div className="mobile-ai-project-content">
          <div className="mobile-ai-project-header">
            <h3 className="mobile-ai-project-title">{displayTitle}</h3>
            <button 
              className="mobile-ai-project-action-btn"
              onClick={(e) => handleDeleteProject(project.id, e)}
              aria-label={t('aiStorage.list.accessibility.deleteProject')}
            >
              <img src={iconDelete} alt={t('aiStorage.list.alt.delete')} />
            </button>
          </div>
          <p className="mobile-ai-project-description">
            {projectDescription.length > 100 
              ? `${projectDescription.substring(0, 100)}...`
              : projectDescription
            }
          </p>
        
          {/* Files thumbnails row */}
          {project.files && project.files.length > 0 && (
            <div className="mobile-ai-project-thumbnails">
              <div className="mobile-ai-thumbnails-row">
                {project.files.slice(0, 8).map((file, index) => {
                  const imageKey = file?.id
                    ? `file-${file.id}`
                    : `${file?.url || file?.thumbnailUrl || index}`;
                  const blurProps = getImageLoadingProps(imageKey, thumbnailScope);
                  const imageSource = file?.url || file?.thumbnailUrl || file?.thumbnail || placeholderImage;

                  return (
                    <div key={file.id || index} className="mobile-ai-thumbnail">
                      {file.type === 'image' ? (
                        <img 
                          src={imageSource} 
                          alt={file.name}
                          className={[
                            'mobile-ai-thumbnail-image',
                            blurProps ? 'mobile-image-with-loading' : '',
                            blurProps?.className || ''
                          ].filter(Boolean).join(' ')}
                          loading="lazy"
                          decoding="async"
                          onLoad={(event) => {
                            blurProps?.onLoad?.(event);
                          }}
                          onError={(event) => {
                            blurProps?.onError?.(event);
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = placeholderImage;
                          }}
                        />
                      ) : file.type === 'video' ? (
                        <div className="mobile-ai-thumbnail-video">
                          <video 
                            src={file.url}
                            className="mobile-ai-thumbnail-video-element"
                            muted
                            preload="metadata"
                            playsInline
                          />
                          <div className="mobile-ai-thumbnail-video-overlay">
                            <img src={iconVideo} alt={t('aiStorage.list.alt.video')} />
                          </div>
                        </div>
                      ) : (
                        <div className="mobile-ai-thumbnail-placeholder">
                          <img src={iconImage} alt={t('aiStorage.list.alt.media')} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {project.files.length > 8 && (
                  <div className="mobile-ai-thumbnail-more">
                    +{project.files.length - 8}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mobile-ai-project-meta">
            <div className="mobile-ai-project-files">
              {project.imagesCount > 0 && (
                <div className="mobile-ai-file-count">
                  <img src={iconImage} alt={t('aiStorage.list.alt.images')} />
                  <span>{project.imagesCount}</span>
                </div>
              )}
              {project.videosCount > 0 && (
                <div className="mobile-ai-file-count">
                  <img src={iconVideo} alt={t('aiStorage.list.alt.videos')} />
                  <span>{project.videosCount}</span>
                </div>
              )}
              {project.imagesCount === 0 && project.videosCount === 0 && project.filesCount > 0 && (
                <div className="mobile-ai-file-count">
                  <img src={iconImage} alt={t('aiStorage.list.alt.media')} />
                  <span>{project.filesCount}</span>
                </div>
              )}
            </div>

            <div className="mobile-ai-project-date">
              {formatProjectDate(project.updatedAt)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render empty state
  const renderEmptyState = () => (
    <div className="mobile-ai-empty-state">
      <div className="mobile-ai-empty-icon">
        <img src={iconAI} alt={t('aiStorage.list.alt.storage')} />
      </div>
      <h3 className="mobile-ai-empty-title">{t('aiStorage.list.empty.title')}</h3>
      <p className="mobile-ai-empty-description">
        {t('aiStorage.list.empty.description')}
      </p>
      <button 
        className="mobile-ai-empty-cta"
        onClick={handleCreateProject}
      >
        {t('aiStorage.list.empty.cta')}
      </button>
    </div>
  );

  const renderLoadingSkeleton = () => {
    const skeletonItems = Array.from({ length: 3 });
    return (
      <div className="mobile-ai-skeleton-list">
        {skeletonItems.map((_, index) => (
          <div key={`skeleton-${index}`} className="mobile-ai-project-card skeleton">
            <div className="mobile-ai-project-header">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton width="70%" height={18} shimmer />
                <Skeleton width="40%" height={14} shimmer />
              </div>
              <Skeleton width={28} height={28} variant="circle" shimmer />
            </div>
            <div className="mobile-ai-project-description skeleton-lines">
              <Skeleton count={2} height={12} shimmer />
            </div>
            <div className="mobile-ai-project-thumbnails">
              <div className="mobile-ai-thumbnails-row">
                {Array.from({ length: 6 }).map((__, thumbIndex) => (
                  <div key={`thumb-${thumbIndex}`} className="mobile-ai-thumbnail">
                    <Skeleton width="100%" height="100%" variant="rounded" shimmer />
                  </div>
                ))}
              </div>
            </div>
            <div className="mobile-ai-project-meta">
              <div className="mobile-ai-project-files">
                <Skeleton width={60} height={12} shimmer />
              </div>
              <Skeleton width={80} height={12} shimmer />
            </div>
          </div>
        ))}
      </div>
    );
  };

  let content = null;

  // Render based on current view
  if (currentView === 'detail' || currentProjectId) {
    content = (
      <ProjectDetailPage 
        projectId={currentProjectId || selectedProjectId}
        onBack={currentView === 'detail' ? onBack : handleBackToList}
      />
    );
  } else {
    content = (
      <div className="mobile-ai-storage">
        <div className="mobile-ai-storage-content">
          {/* Header */}
          <div className="mobile-ai-storage-header">
            <h1 className="mobile-ai-storage-title">{t('aiStorage.header.title')}</h1>
            <p className="mobile-ai-storage-subtitle">
              {t('aiStorage.header.subtitle')}
            </p>
          </div>

          <div className="mobile-ai-gallery-preview-wrapper">
            <MobileGalleryPreview
              rows={3}
              columns={5}
              onOpenGallery={handleOpenGalleryPreview}
              instanceId="ai-storage"
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mobile-ai-error">
              {error}
              <button 
                className="mobile-ai-retry-btn"
                onClick={loadProjects}
              >
                {t('aiStorage.actions.retry')}
              </button>
            </div>
          )}

          {/* Content */}
          <div className="mobile-ai-content">
            {isLoading ? (
              renderLoadingSkeleton()
            ) : projects.length === 0 ? (
              renderEmptyState()
            ) : (
              <>
                {/* Projects list */}
                <div className="mobile-ai-projects-list">
                  {projects.map(renderProjectCard)}
                </div>
              </>
            )}
          </div>

          {/* Floating action button */}
          {projects.length > 0 && (
            <button 
              className="mobile-ai-fab"
              onClick={handleCreateProject}
              disabled={showCreateModal}
              aria-label={t('aiStorage.list.accessibility.createProject')}
            >
              <div className="mobile-ai-fab-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
          )}
        </div>

        {/* Create project modal */}
        <CreateProjectModal 
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onProjectCreated={handleProjectCreated}
        />
      </div>
    );
  }

  return (
    <>
      {content}
      {showImageGallery && (
        <div
          className="mobile-image-gallery-overlay"
          role="dialog"
          aria-modal="true"
          data-mobile-swipe-blocker="true"
        >
          <ImageGalleryPage
            onBack={handleCloseGalleryPreview}
            viewerOrigin="ai-storage"
          />
        </div>
      )}
    </>
  );
};

export default MobileAIStorage;
